import { withShareRef } from './refTag.js'

// ── shareOutward — the one way NextEp leaves the app ─────────────────────────
// Every outward share used to hand navigator.share BOTH `text` and `url`. In iMessage
// that produces TWO bubbles: your sentence, then a separate link preview. Jason's
// screenshot of a Watch Together invite made the cost obvious — a sentence saying
// "Watch X with me on NextEp, live 🍿" sitting above a generic card that said "Never miss
// an episode", i.e. a card about the product rather than the thing being shared. That is
// the first impression of the whole app for someone who has never heard of it
// (2026-07-27).
//
// THE RULE: if the destination already unfurls into a good card, the card IS the message.
// Sending a sentence alongside it splits one strong bubble into two weak ones.
//
// Three cases, in priority order:
//   1. An IMAGE file  → share the file. Posting the actual card beats any link preview,
//                       so Cliffhanger/Wrapped/collection shares keep doing this.
//   2. A RICH url     → share the URL ALONE (single rich bubble). "Rich" means a route
//                       with an unfurl edge function — see RICH_ROUTES, which must track
//                       the [[edge_functions]] entries in netlify.toml.
//   3. Anything else  → text + url, exactly as before. No route, no card, so the sentence
//                       is all the recipient gets.
//
// Always returns a string so callers can drive their own UI:
//   'shared' | 'copied' | 'cancelled' | 'failed'

// Routes that render a real unfurl card. KEEP IN SYNC with netlify.toml's [[edge_functions]]
// — a route listed here without a matching edge function would send a bare link with the
// generic site card, which is strictly worse than the sentence it replaced.
export const RICH_ROUTES = [/^\/show\//, /^\/aftershow\//, /^\/u\//, /^\/share\//, /^\/join\//, /^\/invite\//]

export function isRichUrl(url) {
  try { return RICH_ROUTES.some(re => re.test(new URL(url, window.location.origin).pathname)) }
  catch { return false }
}

// `source` (optional) tags the URL with ?ref=share-<source> before it ever leaves the
// app, so a signup driven by this share shows up as its own row in admin_attribution()
// instead of blending into "direct" — see lib/refTag.js for why this exists.
export async function shareOutward({ url, text, title, file, source } = {}) {
  if (url && source) url = withShareRef(url, source)
  const rich = url ? isRichUrl(url) : false

  // 1. An image says more than a link preview ever will.
  if (file && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ ...(title ? { title } : {}), ...(text ? { text } : {}), files: [file] })
      return 'shared'
    } catch (e) {
      if (e?.name === 'AbortError') return 'cancelled'
      // fall through — a file share can fail on size/type; the link is still worth sending
    }
  }

  if (navigator.share && url) {
    try {
      // The `text` is dropped ON PURPOSE for rich URLs. Passing `url` (not a string that
      // merely contains one) is also what keeps iMessage in single-rich-bubble mode.
      await navigator.share(rich
        ? { ...(title ? { title } : {}), url }
        : { ...(title ? { title } : {}), ...(text ? { text } : {}), url })
      return 'shared'
    } catch (e) {
      if (e?.name === 'AbortError') return 'cancelled'
    }
  }

  // No share sheet (desktop, or a browser without it): copy something useful. For a rich
  // URL the link alone still unfurls wherever it's pasted; otherwise keep the sentence.
  const clip = rich ? url : [text, url].filter(Boolean).join(' ')
  if (clip && navigator.clipboard?.writeText) {
    try { await navigator.clipboard.writeText(clip); return 'copied' } catch { /* ignore */ }
  }
  return 'failed'
}
